// The executable guidance ROOTS — every guidance module a task can be told to run.
//
// A root's identity is its PATH under src/guidance, e.g. 'polish/security/script-review.ts'. There used
// to be a second, parallel namespace: src/guidance/manifest.json gave every entry an arbitrary key
// ('polish-script-security-review'), the deploy bundled one archive per key, and a task carried the key.
// That namespace was the shape of the retired guidance_entries DB table, where the key WAS the primary
// key. With guidance as repo source the path already identifies the module, and the second namespace
// bought nothing but a chance to disagree with itself:
//
//   • Producers built keys by interpolation (`polish-script-${passType}`) because keys mostly followed a
//     convention. The security pass is the one whose roots do not — it has TWO (review + probe) — so
//     every security Pass-2 task asked for 'polish-script-security', got nothing back, and died at
//     startup. Nothing caught it: no check compared a REQUESTED key against the manifest.
//   • 248 archives were built and shipped per deploy; about a dozen keys were ever requested.
//
// So: producers name a root through the typed constants below (a typo is a type error), the value that
// travels to a container is the path, and the container builds that path out of its own checkout.
//
// This module is dependency-free apart from the pass config it derives polish roots from — it is
// imported by backend functions, container harnesses, and guidance scripts alike.
import passesConfig from './polish/runner/passes.json'

// Roots named directly by the backend. The property names are code-level identifiers only: nothing
// persists them, so renaming one is a local refactor.
export const ROOTS = {
  playwrightFailure: 'playwright-failure/script.ts',
  explorationRun: 'exploration/run-script.ts',
  journeyRun: 'journey/run-script.ts',
  journeyRepair: 'journey-repair/script.ts',
  // Materialized next to the journey runner as its `./steps.ts` sibling (run-recorded-browse.ts).
  journeySteps: 'journey/steps.ts',
  versionAddedRun: 'chat/version-added-run-script.ts',
  fileJourneyBug: 'journey/file-journey-bug-script.ts',
  cloneBuild: 'clone/build-script.ts',
  fixMismatchReview: 'fix-mismatch-review/script.ts',
  sessionReviewer: 'session-reviewers/script.ts',
  sessionSummarizer: 'session-summarizers/script.ts',
  userSessionAuthor: 'user-session/author-script.ts',
  evalInstantiate: 'evals/instantiate-script.ts',
  evalProcess: 'evals/process-script.ts',
  evalVerdict: 'evals/verdict-script.ts',
  criticRun: 'admin/critic-run-script.ts',
  adversary: 'admin/adversary/script.ts',
  errorProcessing: 'feedback/error-processing-script.ts',
  // Not a task root: the in-page recorder script, built as a browser IIFE rather than run under Node.
  // Listed here so the one place that names it is the same place as every other root.
  recordingPolyfills: 'recording/injected-polyfills.ts',
  recordingInjected: 'recording/injected-script.ts',
} as const

// The polish passes' roots, read from the pass config so the config stays the single place that says
// which script a pass runs (a pass may declare several — security runs a passive review and an active
// probe). Paths here are validated against the filesystem by scripts/check-guidance-roots.ts.
interface PassConfigShape {
  passes?: Array<{ id?: string; tasks?: Array<{ scriptGuidance?: string }> }>
}

export function polishRootsFor(passType: string): string[] {
  const pass = ((passesConfig as PassConfigShape).passes ?? []).find((p) => p.id === passType)
  return (pass?.tasks ?? []).map((t) => t.scriptGuidance).filter((s): s is string => typeof s === 'string' && s.length > 0)
}

// The followup LABEL for one of a pass's Pass-2 roots. A recording followup is deduped per (owner, label)
// — spawning twice with the same label returns the FIRST task rather than a second one — so a pass with
// several roots must give each a distinct label or every root after the first is silently discarded. A
// single-root pass keeps its bare pass name (the label a task's page shows, and the value in flight).
export function polishFollowupLabel(passType: string, root: string): string {
  if (polishRootsFor(passType).length < 2) return passType
  const leaf = root.split('/').pop()?.replace(/\.[cm]?[jt]s$/, '') ?? root
  return `${passType}:${leaf}`
}

const POLISH_ROOT_PATHS: string[] = ((passesConfig as PassConfigShape).passes ?? []).flatMap((p) =>
  (p.tasks ?? []).map((t) => t.scriptGuidance).filter((s): s is string => typeof s === 'string' && s.length > 0),
)

/** Every path a task may legitimately be asked to run. */
export const GUIDANCE_ROOT_PATHS: readonly string[] = [...new Set([...Object.values(ROOTS), ...POLISH_ROOT_PATHS])]

const ROOT_SET = new Set(GUIDANCE_ROOT_PATHS)

// LEGACY. Task rows queued before roots became paths hold a manifest key in tasks.followup_script, and a
// container picking one of those up after this deploy must still run the right module. Read-only
// compatibility: nothing writes these any more. Delete once no queued task predates the change (the
// queue drains in minutes; the map costs nothing until then).
const LEGACY_ROOT_KEYS: Readonly<Record<string, string>> = {
  'clone-build-script': ROOTS.cloneBuild,
  'critic-run-script': ROOTS.criticRun,
  'error-processing-script': ROOTS.errorProcessing,
  'eval-instantiate-script': ROOTS.evalInstantiate,
  'eval-process-script': ROOTS.evalProcess,
  'eval-verdict-script': ROOTS.evalVerdict,
  'exploration-run-script': ROOTS.explorationRun,
  'file-journey-bug-script': ROOTS.fileJourneyBug,
  'journey-run-script': ROOTS.journeyRun,
  'journey-steps': ROOTS.journeySteps,
  'recording-injected-script': ROOTS.recordingInjected,
  'version-added-run-script': ROOTS.versionAddedRun,
  'polish-script-accessibility': 'polish/accessibility/script.ts',
  'polish-script-glitches': 'polish/glitches/script.ts',
  'polish-script-layout-shift': 'polish/layout-shift/script.ts',
  'polish-script-network-performance': 'polish/network-performance/script.ts',
  'polish-script-react-rendering': 'polish/react-rendering/script.ts',
  'polish-script-seo': 'polish/seo/script.ts',
  'polish-script-security-probe': 'polish/security/script-probe.ts',
  'polish-script-security-review': 'polish/security/script-review.ts',
  'polish-script-ui-details': 'polish/ui-details/script.ts',
  'polish-script-user-experience': 'polish/user-experience/script.ts',
}

/**
 * The root path for a stored/received value: a path passes through, a legacy manifest key maps forward,
 * anything else is null so the caller can fail loudly instead of asking the container to run a name that
 * does not resolve (which is how a missing root used to surface: 4 seconds into a container, as a failed
 * task with no filed bug).
 */
export function resolveGuidanceRoot(value: string | null | undefined): string | null {
  if (!value) return null
  if (ROOT_SET.has(value)) return value
  return LEGACY_ROOT_KEYS[value] ?? null
}
